import { Component } from '@theme/component';
import {
  hasOtherScrollLockClaimants,
  hasVisibleFocus,
  isClickedOutside,
  muteFocusRing,
  onAnimationEnd,
} from '@theme/utilities';

/**
 * A drawer that opens from the right side and overlays the page.
 *
 * The panel is always a modal dialog (`showModal()`): the browser provides a
 * native focus trap, top-layer stacking, and a `::backdrop` we blur to keep
 * focus on the overlay. Background scroll is disabled via the `scroll-lock`
 * attribute on <html> while any drawer is open.
 *
 * On wide viewports (≥ 990px) the drawer is a lateral panel over a blurred
 * page. On narrow viewports (< 990px) it covers the full viewport with a
 * solid background (see theme-drawer-styles.liquid). Both modes are the same
 * modal dialog — only the CSS sizing differs.
 *
 * Focus moves to the panel itself on open and returns to the trigger on close.
 *
 * Dispatches {@link DrawerOpenEvent} and {@link DrawerCloseEvent}.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel - The dialog that owns all visual styles and animation.
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  /** Monotonically increasing counter for z-index stacking of multiple open drawers. */
  static #stackOrder = 0;

  /** @type {boolean} */
  #isClosing = false;

  /** When true, the drawer will reopen after the current close animation finishes. */
  /** @type {{ enter?: 'inline-start' } | false} */
  #deferredOpen = false;

  /** Element to restore focus to when the drawer closes. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /**
   * Whether whatever opened the drawer was already carrying a focus ring, which is the one
   * case where the ring belongs back on it once the drawer closes.
   */
  #triggerWasRinged = false;

  /**
   * @returns {boolean} Whether the drawer is currently open.
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();

    // Sync the static stack counter with any --drawer-stack-order already set
    // on this element (e.g. by a server-rendered open state).
    const restoredOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  /**
   * Restore path: the drawer was rendered with [open] already set. Present the
   * modal and wire the close listeners without moving focus — the user is
   * loading a fresh page and expects focus on main content.
   */
  #onRestore() {
    const { panel } = this.refs;
    if (!panel.open) {
      document.documentElement.setAttribute('scroll-lock', '');
      panel.showModal();
    }
    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.isOpen && !hasOtherScrollLockClaimants()) {
      document.documentElement.removeAttribute('scroll-lock');
    }
    this.#removeEventListeners();
    this.#deferredOpen = false;
  }

  /**
   * The Section Rendering API can re-render a section that contains this drawer
   * (e.g. ticking a filter from inside the filters drawer re-renders the whole
   * facets section in 'full' morph mode). morph reconciles the <dialog> and
   * copies its `open` attribute across, but the top-layer/modal state a dialog
   * gets from showModal() is runtime-only — it is not carried by any attribute.
   * If a morph ever detaches and re-inserts the dialog, it comes back `open` but
   * non-modal: no ::backdrop, no top-layer stacking, no focus trap, and the page
   * behind it becomes clickable. Re-assert modal state here to keep the
   * always-modal invariant. The `:modal` guard makes this a no-op on the common
   * path where morph reconciles the dialog in place and modal state survives.
   */
  updatedCallback() {
    super.updatedCallback();

    const { panel } = this.refs;
    // Skip if this fired on a detached node (never connected to the document)
    // or if the drawer isn't open — showModal() on a disconnected dialog throws.
    if (!this.isOpen || !panel.isConnected || panel.matches(':modal')) return;

    // showModal() throws if the dialog already carries `open`, so drop out of
    // the (now non-modal) open state first, then re-enter the top layer.
    if (panel.open) panel.close();
    panel.showModal();
  }

  /**
   * Closes the drawer when the user clicks the backdrop (outside the dialog
   * content area). Modal dialogs register backdrop clicks on the dialog itself.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    // A listener earlier in the dispatch can detach the clicked element (the
    // tracking drawer's "Track another order" button removes its own result
    // panel), and contains() reads a detached target as outside, closing the
    // drawer under the user. The dialog is modal, so a genuinely outside click
    // can only ever arrive with the dialog itself as target — a detached
    // target therefore always started inside and must not dismiss.
    if (event.target instanceof Node && !event.target.isConnected) return;

    // A modal opened from inside the drawer — the media gallery's zoom is the first
    // one — is a descendant of this panel, so its clicks bubble here, and the ones
    // that land beside its content arrive with that dialog as their target. The
    // coordinate branch below then measures them against this panel's box and reads
    // them as a click on the drawer's own backdrop, closing the drawer out from
    // under whatever is covering it. A click on another dialog is inside that
    // dialog, never on this one's backdrop.
    if (event.target instanceof HTMLDialogElement && event.target !== panel) return;

    if (isClickedOutside(event, panel)) {
      this.close();
    }
  };

  /**
   * Closes the drawer when the user presses Escape.
   * preventDefault() suppresses the native dialog cancel event.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    // isComposing: don't close while an Input Method Editor (e.g. Japanese,
    // Chinese, Korean input) consumes Escape to dismiss its candidate list —
    // can happen inside the cart's discount-code field.
    // defaultPrevented: let nested components handle Escape first.
    if (event.isComposing || event.defaultPrevented) return;
    if (!this.isOpen) return;
    if (!this.#isTopmost()) return;

    event.preventDefault();
    this.close();
  };

  /**
   * Shows the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction. `'inline-start'` slides left-to-right (used when the chat docks).
   */
  open({ enter } = {}) {
    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }
    if (this.#isClosing) {
      this.#deferredOpen = { enter };
      return;
    }

    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);

    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);
    // Read now: this is the last moment the browser's answer still describes how the
    // shopper reached the trigger rather than what they did inside the drawer.
    this.#triggerWasRinged = hasVisibleFocus(document.activeElement);

    document.documentElement.setAttribute('scroll-lock', '');
    panel.showModal();

    this.#focusInitialElement();

    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    this.setAttribute('open', '');
    this.dispatchEvent(new DrawerOpenEvent());

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  /**
   * Bumps this drawer's z-index to the top of the stack and replays
   * the slide-in animation. Called when open() is invoked on an
   * already-open drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction.
   */
  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    // Modal dialogs live in the browser's top layer where z-index is ignored —
    // stacking follows showModal() call order. Re-calling showModal() moves
    // this dialog to the top of the stack.
    if (panel.open) {
      panel.close();
      panel.showModal();
    }

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);
    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    // Re-fire so listeners (e.g. chat mode sync) pick up the new stack order.
    this.dispatchEvent(new DrawerOpenEvent());
  }

  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener('keydown', this.#onKeyDown);
    panel.removeEventListener('click', this.#onBackdropClick);
  }

  /**
   * Moves focus into the drawer, onto the panel itself rather than the first
   * control inside it.
   *
   * The close button is what a dialog's first focusable descendant almost always
   * is — it is the first thing in theme-drawer-header, in quick add's header row,
   * in every panel here — so both routes into an open drawer, the explicit focus
   * this method used to do and `showModal()`'s own focusing steps when it did not,
   * land on the same button. Focusing a control on open paints its focus ring:
   * WebKit matches `:focus-visible` on a programmatic focus that follows a tap,
   * and the ring is drawn `outline-offset` *outside* the button, which in a panel
   * whose close button sits flush against the panel edge (quick add's mobile
   * header row is the one in the screenshots) is a ring hanging half off the
   * viewport the moment the drawer opens.
   *
   * The panel is the better target regardless: focusing the dialog is what the
   * ARIA authoring practices ask for when the first control is a dismiss button —
   * a screen reader then announces the drawer by its own `aria-label` /
   * `aria-labelledby` instead of reading "Close" — and Tab from it still lands on
   * that close button first, with a real ring, for keyboard users. `tabindex` is
   * set here rather than in each panel's markup so every drawer, including ones
   * added later, gets it for free, and re-set on every open because a Section
   * Rendering API morph reconciles the dialog against server HTML that has no
   * such attribute.
   *
   * `preventScroll` for the same reason frame.js uses it: focus must never
   * scroll anything, least of all mid slide-in, when the panel is still
   * off-screen and the browser would scroll the page sideways to chase it.
   */
  #focusInitialElement() {
    const { panel } = this.refs;

    panel.setAttribute('tabindex', '-1');
    panel.focus({ preventScroll: true });
  }

  /**
   * Slides the drawer out, waits for the animation, then closes the dialog.
   */
  async #closeDialog() {
    if (!this.isOpen) return;
    if (this.#isClosing) return;
    this.#isClosing = true;

    this.#removeEventListeners();

    const { panel } = this.refs;

    this.removeAttribute('open');
    this.dispatchEvent(new DrawerCloseEvent());

    // Only remove scroll-lock if nothing else (another theme-drawer, a menu
    // drawer's <details scroll-lock>, or a modal dialog) still needs it.
    if (!hasOtherScrollLockClaimants()) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    if (panel.open) {
      // Cancel any in-progress open animation before starting the close.
      panel.classList.remove('theme-drawer__dialog--opening', 'theme-drawer__dialog--opening-inline-start');

      panel.classList.add('theme-drawer__dialog--closing');
      await onAnimationEnd(panel, undefined, { subtree: false });
      panel.classList.remove('theme-drawer__dialog--closing');
    }

    panel.close();
    this.style.removeProperty('--drawer-stack-order');

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;
    // A Section Rendering API morph between open and close can replace the
    // trigger node. The JS reference stays valid but the element is detached
    // from the live DOM — and .focus() on a detached node is a silent no-op,
    // so we explicitly check and fall back to a fresh query.
    if (trigger && document.contains(trigger)) {
      trigger.focus();
    } else {
      /** @type {HTMLElement | null} */ (document.querySelector(`[aria-controls="${this.id}"]`))?.focus();
    }

    // The browser rings the returned-to trigger whenever the keyboard was the last thing
    // used — typing into a field in the drawer is enough — so the ring is muted unless the
    // trigger was already wearing one when it opened the drawer (see `muteFocusRing`).
    if (!this.#triggerWasRinged) muteFocusRing(document.activeElement);

    this.#isClosing = false;

    // Reconcile: if open() was called during the close animation, reopen now.
    if (this.#deferredOpen) {
      const options = this.#deferredOpen;
      this.#deferredOpen = false;
      this.open(options);
    }
  }

  /**
   * Closes the drawer.
   */
  async close() {
    if (this.#isClosing) {
      // A close is already in progress — cancel any deferred open so the
      // drawer stays closed when the animation finishes.
      this.#deferredOpen = false;
      return;
    }
    await this.#closeDialog();
  }

  /**
   * Toggles the drawer. If the drawer is open but not the topmost in the
   * stack, it is brought to front instead of closed.
   */
  toggle() {
    if (this.isOpen && this.#isTopmost()) {
      return this.close();
    }
    return this.open();
  }

  /**
   * @returns {boolean} Whether this drawer has the highest stack order among all open drawers.
   */
  #isTopmost() {
    const myOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    for (const other of document.querySelectorAll('theme-drawer[open]')) {
      if (other === this) continue;
      const otherOrder = Number(/** @type {HTMLElement} */ (other).style.getPropertyValue('--drawer-stack-order') || 0);
      if (otherOrder > myOrder) return false;
    }
    return true;
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:close';
}
